import * as THREE from 'three';
import { ORDER } from '../core/systems.js';
import { buildCloudTexture } from './fx/noisegen.js';

/**
 * Sky & atmosphere.
 *
 * The dome is a Preetham analytic scattering model: turbidity drives the
 * Perez distribution coefficients and the zenith chromaticity, which are
 * solved on the CPU once per sun move and evaluated per fragment. That gives
 * a real horizon glow, a real aureole around the sun and a real cool zenith
 * — none of which a two-stop gradient can fake.
 *
 * On top of it sits a two-layer cloud sheet sampled from a tileable field
 * generated in code (see fx/noisegen.js). Each layer is lit by marching a
 * few taps towards the sun through the sheet, so cumulus get bright tops,
 * dark bases and a silver lining when they cross the sun.
 *
 * The same model is evaluated on the CPU (`sample()`), which is how the
 * lighting rig gets its key/fill/ambient colours and the post chain gets its
 * aerial-perspective tint. Everything downstream is therefore driven by one
 * source of truth: the sun direction.
 *
 * Publishes `world.sky`.
 */

const DEG = Math.PI / 180;

/* ------------------------------------------------------- the Preetham model */

/**
 * Perez distribution coefficients for luminance Y and chromaticity x,y as a
 * function of turbidity. Straight out of Preetham et al. 1999, table 1.
 */
function perezCoefficients(T, out) {
  out[0] = 0.1787 * T - 1.4630;  // AY
  out[1] = -0.3554 * T + 0.4275; // BY
  out[2] = -0.0227 * T + 5.3251; // CY
  out[3] = 0.1206 * T - 2.5771;  // DY
  out[4] = -0.0670 * T + 0.3703; // EY

  out[5] = -0.0193 * T - 0.2592; // Ax
  out[6] = -0.0665 * T + 0.0008; // Bx
  out[7] = -0.0004 * T + 0.2125; // Cx
  out[8] = -0.0641 * T - 0.8989; // Dx
  out[9] = -0.0033 * T + 0.0452; // Ex

  out[10] = -0.0167 * T - 0.2608; // Ay
  out[11] = -0.0950 * T + 0.0092; // By
  out[12] = -0.0079 * T + 0.2102; // Cy
  out[13] = -0.0441 * T - 1.6537; // Dy
  out[14] = -0.0109 * T + 0.0529; // Ey
  return out;
}

/** Absolute zenith luminance (kcd/m^2) and chromaticity for turbidity+sun. */
function zenithYxy(T, thetaS, out) {
  const chi = (4 / 9 - T / 120) * (Math.PI - 2 * thetaS);
  out[0] = (4.0453 * T - 4.9710) * Math.tan(chi) - 0.2155 * T + 2.4192;
  const t = thetaS, t2 = t * t, t3 = t2 * t, T2 = T * T;
  out[1] = (0.00166 * t3 - 0.00375 * t2 + 0.00209 * t) * T2 +
           (-0.02903 * t3 + 0.06377 * t2 - 0.03202 * t + 0.00394) * T +
           (0.11693 * t3 - 0.21196 * t2 + 0.06052 * t + 0.25886);
  out[2] = (0.00275 * t3 - 0.00610 * t2 + 0.00317 * t) * T2 +
           (-0.04214 * t3 + 0.08970 * t2 - 0.04153 * t + 0.00516) * T +
           (0.15346 * t3 - 0.26756 * t2 + 0.06670 * t + 0.26688);
  return out;
}

const F = (A, B, C, D, E, cosTheta, gamma, cosGamma) =>
  (1 + A * Math.exp(B / Math.max(cosTheta, 0.02))) *
  (1 + C * Math.exp(D * gamma) + E * cosGamma * cosGamma);

/* ------------------------------------------------------------------ presets */

export const SKY_PRESETS = {
  // The house look: sun low and forward-left so every warrior gets a rim.
  golden:   { t: 0.678, turbidity: 3.2, coverage: 0.44, cloudDens: 1.00, tint: [1.00, 1.00, 1.00], gain: 1.00, night: 0.0, mie: 0.86 },
  noon:     { t: 0.500, turbidity: 2.6, coverage: 0.62, cloudDens: 0.85, tint: [1.00, 1.00, 1.00], gain: 0.90, night: 0.0, mie: 0.80 },
  overcast: { t: 0.560, turbidity: 8.0, coverage: 0.10, cloudDens: 1.30, tint: [0.92, 0.95, 1.00], gain: 0.72, night: 0.0, mie: 0.72 },
  storm:    { t: 0.610, turbidity: 9.5, coverage: 0.02, cloudDens: 1.55, tint: [0.78, 0.83, 0.95], gain: 0.52, night: 0.0, mie: 0.70 },
  dusk:     { t: 0.734, turbidity: 4.8, coverage: 0.44, cloudDens: 1.10, tint: [1.04, 0.94, 0.92], gain: 0.86, night: 0.06, mie: 0.90 },
  night:    { t: 0.905, turbidity: 2.2, coverage: 0.58, cloudDens: 0.80, tint: [0.62, 0.72, 1.05], gain: 0.30, night: 1.0, mie: 0.76 },
};

/* --------------------------------------------------------------- the shader */

const VERT = /* glsl */`
  varying vec3 vWorld;
  void main() {
    vec4 wp = modelMatrix * vec4(position, 1.0);
    vWorld = wp.xyz;
    vec4 p = projectionMatrix * viewMatrix * wp;
    // Pin the dome to the far plane and draw it *after* the world: every
    // covered pixel is then rejected by early-z instead of running the whole
    // scattering + cloud shader behind a mountain.
    gl_Position = vec4(p.xy, p.w * 0.999995, p.w);
  }`;

const FRAG = /* glsl */`
  precision highp float;

  uniform vec3  uSunDir;
  uniform vec3  uPerezA, uPerezB, uPerezC, uPerezD, uPerezE; // (Y, x, y)
  uniform vec3  uZenith;        // Yz, xz, yz
  uniform float uFdivY;         // 1 / F(0, thetaS) for Y
  uniform float uFdivX;
  uniform float uFdivZ;
  uniform vec3  uSkyTint;
  uniform float uSkyGain;
  uniform float uNight;
  uniform vec3  uGround;
  uniform vec3  uSunColor;      // HDR radiance of the disc
  uniform float uSunSize;       // angular radius, radians
  uniform sampler2D uClouds;
  uniform vec4  uCloud;         // x coverage, y density, z altitudeK, w wind t
  uniform vec4  uCloud2;        // x cirrus coverage, y cirrus gain, z scale, w scale2
  uniform vec3  uCloudSun;      // sunlit cloud colour
  uniform vec3  uCloudShade;    // shadowed cloud colour
  uniform float uMieG;
  uniform vec2  uAureole;       // x floor, y exponent -- circumsolar rollback

  varying vec3 vWorld;

  const mat3 XYZ2RGB = mat3(
     3.2404542, -0.9692660,  0.0556434,
    -1.5371385,  1.8760108, -0.2040259,
    -0.4985314,  0.0415560,  1.0572252);

  float perez(float A, float B, float C, float D, float E, float cosT, float g, float cosG) {
    return (1.0 + A * exp(B / max(cosT, 0.02))) *
           (1.0 + C * exp(D * g) + E * cosG * cosG);
  }

  vec3 skyRadiance(vec3 d) {
    float cosT = max(d.y, 0.0);
    float cosG = clamp(dot(d, uSunDir), -1.0, 1.0);
    float g = acos(cosG);

    float Y = uZenith.x * perez(uPerezA.x, uPerezB.x, uPerezC.x, uPerezD.x, uPerezE.x, cosT, g, cosG) * uFdivY;
    float x = uZenith.y * perez(uPerezA.y, uPerezB.y, uPerezC.y, uPerezD.y, uPerezE.y, cosT, g, cosG) * uFdivX;
    float y = uZenith.z * perez(uPerezA.z, uPerezB.z, uPerezC.z, uPerezD.z, uPerezE.z, cosT, g, cosG) * uFdivZ;

    Y = max(Y, 0.0);
    y = max(y, 1e-3);
    vec3 XYZ = vec3(x / y * Y, Y, (1.0 - x - y) / y * Y);
    vec3 rgb = XYZ2RGB * XYZ;
    return max(rgb, vec3(0.0)) * 0.045;
  }

  // ---- clouds -------------------------------------------------------------
  // Project the view ray onto a shell above the observer. The 1/(y+k) form is
  // the cheap stand-in for a spherical shell: layers pile up towards the
  // horizon exactly the way real cloud decks do.
  vec2 cloudUV(vec3 d, float k, float scale) {
    float y = max(d.y, 0.0);
    return d.xz * (k / (y + k * 0.055)) * scale;
  }

  float cloudField(vec2 uv, float cov, out float soft) {
    vec4 s = texture2D(uClouds, uv);
    float d = s.r + (s.g - 0.5) * 0.30;
    soft = s.a;
    return smoothstep(cov, cov + 0.20, d);
  }

  void main() {
    vec3 d = normalize(vWorld - cameraPosition);

    vec3 col = skyRadiance(d) * uSkyGain * uSkyTint;

    // Preetham's circumsolar term drives the whole quadrant around the sun
    // past the clipping point, and a white disc on a white sky is not a disc.
    // Rolling the aureole back keeps the golden wash but leaves the disc (and
    // the post chain's solar core) as the only thing in frame that clips.
    //
    // The first attempt at this was broad and shallow -- floor 0.70, exponent
    // 2.2 -- which is 30% at the sun still falling to only 6% at 60 deg, so it
    // dimmed the entire quadrant a little and left the disc where it started.
    // Measured on the composite: the disc core read 249.3 and the sky just
    // outside it 247.0, a contrast of 2.3 levels. The disc cannot be raised
    // (it is already 41x linear and clipping on the ACES shoulder), so the sky
    // has to come down, and it has to come down TIGHTLY or the golden wash the
    // whole frame is lit by goes with it. Hence a deep floor on a high
    // exponent: most of the rollback is spent inside ~25 deg of the sun.
    float aur = max(dot(d, uSunDir), 0.0);
    col *= mix(1.0, uAureole.x, pow(aur, uAureole.y) * (1.0 - uNight));

    // ground half: a dark, sky-tinted haze so the dome never shows a seam
    float below = smoothstep(0.0, -0.14, d.y);
    vec3 groundCol = mix(col * 0.42, uGround, 0.72);

    // ---- sun disc with limb darkening ------------------------------------
    float cosG = dot(d, uSunDir);
    float gamma = acos(clamp(cosG, -1.0, 1.0));
    float r = gamma / uSunSize;
    if (r < 1.0) {
      float mu = sqrt(max(1.0 - r * r, 0.0));
      // classic 5-coefficient limb darkening, collapsed to one term
      float limb = 1.0 - 0.62 * (1.0 - pow(mu, 0.42));
      col += uSunColor * limb * smoothstep(1.0, 0.86, r);
    }
    // aureole: forward Mie scattering rolls off much slower than the disc
    float g2 = uMieG * uMieG;
    float hg = min((1.0 - g2) / pow(1.0 + g2 - 2.0 * uMieG * max(cosG, 0.0), 1.5), 26.0);
    col += uSunColor * 0.00030 * hg * (1.0 - uNight);

    // ---- stars (night moods only) ----------------------------------------
    if (uNight > 0.01 && d.y > -0.02) {
      vec2 sp = floor(d.xz / max(abs(d.y) + 0.14, 0.02) * 190.0);
      float h = fract(sin(dot(sp, vec2(12.9898, 78.233))) * 43758.5453);
      float star = smoothstep(0.9975, 0.99995, h) * (0.4 + 0.6 * fract(h * 91.7));
      col += vec3(0.85, 0.9, 1.0) * star * uNight * 3.2 * smoothstep(0.0, 0.25, d.y);
    }

    // ---- cloud decks ------------------------------------------------------
    if (d.y > -0.03) {
      vec2 sunXZ = normalize(uSunDir.xz + vec2(1e-4));
      float horizonFade = smoothstep(-0.02, 0.10, d.y);

      // mid-level cumulus
      vec2 uv = cloudUV(d, uCloud.z, uCloud2.z) + vec2(uCloud.w, uCloud.w * 0.42);
      float soft;
      float cov = cloudField(uv, uCloud.x, soft);
      if (cov > 0.001) {
        // three taps towards the sun approximate the optical depth through
        // the sheet: bright rim where light gets through, dark where it does not
        // The optical depth is the *coverage* of the taps towards the sun,
        // not their soft channel: reading the out-parameter here fed the
        // extinction a field with a mean of 0.5 everywhere, so every cloud
        // came out fully shadowed and therefore the same colour as the sky
        // behind it — which is why the dome read as a bare gradient.
        float step = 0.020 * uCloud2.z;
        float o1, o2, o3;
        float c1 = cloudField(uv + sunXZ * step, uCloud.x, o1);
        float c2 = cloudField(uv + sunXZ * step * 2.4, uCloud.x, o2);
        float c3 = cloudField(uv + sunXZ * step * 5.0, uCloud.x, o3);
        float tau = (c1 * 0.5 + c2 * 0.32 + c3 * 0.18) * uCloud.y * 2.6;
        float light = exp(-tau * 1.6);
        // vertical shading: the soft channel doubles as thickness
        float body = mix(0.55, 1.0, 1.0 - soft);
        vec3 cc = mix(uCloudShade, uCloudSun, light * body);
        // silver lining when the deck crosses the sun
        cc += uCloudSun * pow(max(cosG, 0.0), 14.0) * light * 1.5;
        float a = cov * uCloud.y * horizonFade;
        a = clamp(a, 0.0, 1.0);
        col = mix(col, cc, a);
      }

      // high cirrus, thinner, faster, more stretched
      vec2 uv2 = cloudUV(d, uCloud.z * 2.1, uCloud2.w) + vec2(uCloud.w * 2.3, -uCloud.w * 0.5);
      float cir = texture2D(uClouds, uv2).b;
      cir = smoothstep(uCloud2.x, uCloud2.x + 0.35, cir) * uCloud2.y * horizonFade;
      if (cir > 0.001) {
        vec3 cc = mix(uCloudShade, uCloudSun, 0.86);
        cc += uCloudSun * pow(max(cosG, 0.0), 8.0) * 0.9;
        col = mix(col, cc, clamp(cir, 0.0, 0.9));
      }
    }

    col = mix(col, groundCol, below);
    gl_FragColor = vec4(max(col, vec3(0.0)), 1.0);
  }`;

/* ------------------------------------------------------------------ system */

const _c = new THREE.Color();
const _v = new THREE.Vector3();
/** THREE.Color has no addScaledVector; this is the same thing. */
const addScaled = (dst, c, k) => { dst.r += c.r * k; dst.g += c.g * k; dst.b += c.b * k; return dst; };

export function createSky() {
  const sunDir = new THREE.Vector3(0.4, 0.65, 0.3).normalize();

  const coef = new Float32Array(15);
  const zen = new Float32Array(3);
  const state = {
    t: 0.70, turbidity: 3.4, coverage: 0.50, cloudDens: 1.0,
    tint: new THREE.Color(1, 1, 1), gain: 1.0, night: 0.0, mie: 0.86,
    elevation: 0, azimuth: 0, wind: 0.0032, cloudT: 0,
    maxElev: 62 * DEG, azStart: 140 * DEG, azEnd: -70 * DEG,
    preset: 'golden', version: 0,
  };

  // Colours other systems read. Updated whenever the sun moves.
  const sunColor = new THREE.Color();       // direct light, linear, un-normalised
  const sunDisc = new THREE.Color();        // HDR radiance drawn in the dome
  const zenithColor = new THREE.Color();
  const horizonColor = new THREE.Color();
  const sunHorizon = new THREE.Color();     // horizon in the sun's azimuth
  const groundColor = new THREE.Color(0x241d15);
  const ambientColor = new THREE.Color();

  let mesh = null, uni = null, cloudTex = null;

  /** Evaluate the analytic sky in the given direction (CPU mirror of the shader). */
  function sample(dx, dy, dz, out) {
    const cosT = Math.max(dy, 0.0);
    let cg = dx * sunDir.x + dy * sunDir.y + dz * sunDir.z;
    cg = cg < -1 ? -1 : cg > 1 ? 1 : cg;
    const g = Math.acos(cg);
    const fY = F(coef[0], coef[1], coef[2], coef[3], coef[4], cosT, g, cg);
    const fx = F(coef[5], coef[6], coef[7], coef[8], coef[9], cosT, g, cg);
    const fy = F(coef[10], coef[11], coef[12], coef[13], coef[14], cosT, g, cg);
    const thetaS = Math.acos(Math.max(0.0, Math.min(1, sunDir.y)));
    const cs = Math.cos(thetaS);
    const f0Y = F(coef[0], coef[1], coef[2], coef[3], coef[4], 1, thetaS, cs);
    const f0x = F(coef[5], coef[6], coef[7], coef[8], coef[9], 1, thetaS, cs);
    const f0y = F(coef[10], coef[11], coef[12], coef[13], coef[14], 1, thetaS, cs);
    let Y = Math.max(0, zen[0] * fY / f0Y);
    const x = zen[1] * fx / f0x;
    const y = Math.max(1e-3, zen[2] * fy / f0y);
    const X = x / y * Y, Z = (1 - x - y) / y * Y;
    let r = 3.2404542 * X - 1.5371385 * Y - 0.4985314 * Z;
    let gg = -0.9692660 * X + 1.8760108 * Y + 0.0415560 * Z;
    let b = 0.0556434 * X - 0.2040259 * Y + 1.0572252 * Z;
    const s = 0.045 * state.gain;
    out.setRGB(Math.max(0, r) * s * state.tint.r, Math.max(0, gg) * s * state.tint.g, Math.max(0, b) * s * state.tint.b);
    return out;
  }

  /** Recompute every derived colour + uniform from (t, turbidity, ...). */
  function refresh() {
    const dayT = (state.t - 0.25) / 0.5;
    const elev = Math.sin(dayT * Math.PI) * state.maxElev;
    const azim = state.azStart + (state.azEnd - state.azStart) * dayT;
    state.elevation = elev; state.azimuth = azim;
    const ce = Math.cos(elev);
    sunDir.set(Math.sin(azim) * ce, Math.sin(elev), Math.cos(azim) * ce).normalize();

    const thetaS = Math.acos(Math.max(-0.2, Math.min(1, sunDir.y)));
    // Preetham's fit degenerates below the horizon; hold it at the horizon and
    // let the night tint take over instead.
    const tS = Math.min(thetaS, 1.52);
    perezCoefficients(state.turbidity, coef);
    zenithYxy(state.turbidity, tS, zen);

    const cs = Math.cos(tS);
    const f0Y = F(coef[0], coef[1], coef[2], coef[3], coef[4], 1, tS, cs);
    const f0x = F(coef[5], coef[6], coef[7], coef[8], coef[9], 1, tS, cs);
    const f0y = F(coef[10], coef[11], coef[12], coef[13], coef[14], 1, tS, cs);

    if (uni) {
      uni.uSunDir.value.copy(sunDir);
      uni.uPerezA.value.set(coef[0], coef[5], coef[10]);
      uni.uPerezB.value.set(coef[1], coef[6], coef[11]);
      uni.uPerezC.value.set(coef[2], coef[7], coef[12]);
      uni.uPerezD.value.set(coef[3], coef[8], coef[13]);
      uni.uPerezE.value.set(coef[4], coef[9], coef[14]);
      uni.uZenith.value.set(zen[0], zen[1], zen[2]);
      uni.uFdivY.value = 1 / f0Y;
      uni.uFdivX.value = 1 / f0x;
      uni.uFdivZ.value = 1 / f0y;
      uni.uSkyTint.value.copy(state.tint);
      uni.uSkyGain.value = state.gain;
      uni.uNight.value = state.night;
      uni.uMieG.value = state.mie;
      uni.uCloud.value.x = 1.0 - state.coverage;
      uni.uCloud.value.y = state.cloudDens;
      uni.uCloud2.value.x = 1.0 - state.coverage * 0.55;
      uni.uCloud2.value.y = 0.55 * (1.0 - state.night * 0.5);
    }

    // ---- derived light colours ------------------------------------------
    // Direct sunlight = the solar spectrum after the same air mass the sky
    // model assumes. Approximated with a Rayleigh-weighted extinction so the
    // key light goes deep amber exactly when the horizon does.
    const h = Math.max(sunDir.y, 0.0);
    const airMass = 1.0 / (h + 0.15);
    const ext = (b, m) => Math.exp(-b * m * (0.35 + state.turbidity * 0.055));
    sunColor.setRGB(ext(0.135, airMass), ext(0.32, airMass), ext(0.78, airMass));
    const above = Math.max(0, Math.min(1, (sunDir.y + 0.055) / 0.20));
    sunColor.multiplyScalar(above);

    // HDR disc radiance: hot enough that the bloom bright-pass really bites
    sunDisc.copy(sunColor).multiplyScalar(22.0 + 42.0 * h);
    sunDisc.r = Math.max(sunDisc.r, 0.4 * above);

    sample(0, 1, 0, zenithColor);
    sample(sunDir.x, 0.02, sunDir.z, sunHorizon);
    _v.set(-sunDir.x, 0.02, -sunDir.z).normalize();
    sample(_v.x, _v.y, _v.z, horizonColor);
    // hemisphere-integrated-ish ambient: zenith and both horizons, weighted
    ambientColor.copy(zenithColor).multiplyScalar(0.45);
    addScaled(ambientColor, sunHorizon, 0.33);
    addScaled(ambientColor, horizonColor, 0.22);

    if (uni) {
      uni.uSunColor.value.copy(sunDisc);
      uni.uGround.value.copy(groundColor).multiplyScalar(0.35 + 0.9 * h);
      // clouds are lit by the sun and filled by the sky
      _c.copy(sunColor).multiplyScalar(1.55 + 1.4 * h);
      addScaled(_c, zenithColor, 0.35);
      uni.uCloudSun.value.copy(_c);
      _c.copy(zenithColor).multiplyScalar(0.62);
      addScaled(_c, sunHorizon, 0.18);
      _c.r = Math.max(_c.r, 0.012); _c.g = Math.max(_c.g, 0.014); _c.b = Math.max(_c.b, 0.02);
      uni.uCloudShade.value.copy(_c);
    }
    state.version++;
  }

  const api = {
    sunDir, sunColor, sunDisc, zenithColor, horizonColor, sunHorizon, groundColor, ambientColor,
    state, sample,
    get version() { return state.version; },
    get elevation() { return state.elevation; },
    /** t in [0,1]: 0.25 sunrise, 0.5 noon, 0.75 sunset. */
    setTimeOfDay(t) { state.t = t; refresh(); },
    setTurbidity(v) { state.turbidity = v; refresh(); },
    /**
     * Circumsolar rollback: how far the sky around the sun is pulled down so
     * the disc has something to stand above. Exposed for the same reason
     * `pipeline.bloomThreshold` is -- the alternative to a sweep is a rebuild
     * and a fresh capture per value, and a shader constant cannot be measured
     * in a frozen frame.
     */
    setAureole(floorV, expV) {
      const u = uni && uni.uAureole;
      if (!u) return null;
      if (floorV !== undefined) u.value.x = +floorV;
      if (expV !== undefined) u.value.y = +expV;
      return [u.value.x, u.value.y];
    },
    setCoverage(v) { state.coverage = v; refresh(); },
    setPreset(name) {
      const p = SKY_PRESETS[name] || SKY_PRESETS.golden;
      state.preset = SKY_PRESETS[name] ? name : 'golden';
      state.t = p.t; state.turbidity = p.turbidity; state.coverage = p.coverage;
      state.cloudDens = p.cloudDens; state.gain = p.gain; state.night = p.night; state.mie = p.mie;
      state.tint.setRGB(p.tint[0], p.tint[1], p.tint[2]);
      refresh();
    },
    group: null, uniforms: null,
  };

  return {
    name: 'sky', order: ORDER.WORLD,
    init(w) {
      cloudTex = buildCloudTexture(512, 40917);
      cloudTex.anisotropy = Math.min(4, w.renderer?.capabilities?.getMaxAnisotropy?.() ?? 1);

      uni = {
        uSunDir: { value: sunDir },
        uPerezA: { value: new THREE.Vector3() }, uPerezB: { value: new THREE.Vector3() },
        uPerezC: { value: new THREE.Vector3() }, uPerezD: { value: new THREE.Vector3() },
        uPerezE: { value: new THREE.Vector3() },
        uZenith: { value: new THREE.Vector3() },
        uFdivY: { value: 1 }, uFdivX: { value: 1 }, uFdivZ: { value: 1 },
        uSkyTint: { value: new THREE.Color(1, 1, 1) },
        uSkyGain: { value: 1 },
        uNight: { value: 0 },
        uGround: { value: new THREE.Color(0x241d15) },
        uSunColor: { value: new THREE.Color(1, 1, 1) },
        uSunSize: { value: 0.0152 },
        uClouds: { value: cloudTex },
        uCloud: { value: new THREE.Vector4(0.5, 1.0, 0.34, 0) },
        uCloud2: { value: new THREE.Vector4(0.5, 0.55, 0.55, 0.30) },
        uCloudSun: { value: new THREE.Color(1, 1, 1) },
        uCloudShade: { value: new THREE.Color(0.2, 0.24, 0.32) },
        uMieG: { value: 0.86 },
        uAureole: { value: new THREE.Vector2(0.70, 2.2) },
      };

      const geo = new THREE.SphereGeometry(600, 40, 24);
      const mat = new THREE.ShaderMaterial({
        side: THREE.BackSide, depthWrite: false, depthTest: true, fog: false,
        uniforms: uni, vertexShader: VERT, fragmentShader: FRAG,
      });
      mesh = new THREE.Mesh(geo, mat);
      mesh.frustumCulled = false;
      mesh.renderOrder = 900;
      mesh.matrixAutoUpdate = false;
      w.scene.add(mesh);

      api.group = mesh; api.mesh = mesh; api.uniforms = uni;
      api.setPreset('golden');
      w.sky = api;
    },

    update(w, dt, alpha, realDt) {
      state.cloudT += (realDt || dt) * state.wind;
      uni.uCloud.value.w = state.cloudT;
    },

    lateUpdate(w) {
      // keep the dome centred on the viewer so the ray direction stays exact
      const cp = w.camera.position;
      mesh.position.set(cp.x, cp.y, cp.z);
      mesh.updateMatrix();
      mesh.updateMatrixWorld(true);
    },

    dispose() {
      mesh?.geometry.dispose();
      mesh?.material.dispose();
      cloudTex?.dispose();
    },
  };
}
